import { Row } from "./Row";
interface IProsp {
  isShow: boolean;
  onToggle: () => void;
}

export const PersonalInformationUses = ({ onToggle, isShow }: IProsp) => {
  return (
    <Row title="Personal Information Uses" onToggle={onToggle} isShow={isShow}>
      <div>
        We may use any anonymized or aggregated data that incorporates or is
        derived from your personal information (which is not itself personal
        information) after you update or delete it; however, we will not do so
        in a way that identifies you personally. Otherwise, this section
        describes the uses for the personal information we collect.
        <div className="font-bold mt-4">
          a. Uses of Information Collected By Communications
        </div>
        <div className="mt-2">
          We use personal information in your communications to communicate with
          you and respond to your requests, comments, or questions.
        </div>
        <div className="font-bold mt-4">
          b. Uses of Information Collected by Website Visitors
        </div>
        <div className="mt-2">
          We use information collected from website visitors to:
        </div>
        <ul className="list-disc pl-8 mt-2 text-gray666 dark:text-white60">
          <li>Improve our website quality, reliability, and responsiveness</li>
          <li>Improve users’ experience using the website</li>
          <li>Identify problems with our website</li>
          <li>Understand our users’ preferences</li>
        </ul>
        <div className="font-bold mt-4">
          c. Uses of Information Collected from Users and User Devices
        </div>
        <div className="p-2">
          <div className="font-bold my-2">
            i. Uses of Information Collected in Connection with Obtaining
            Applications
          </div>
          As described in Section 4(c)(i) above, Medcursor collects device
          information (not personal information) to provide you with the
          Medcursor mobile application and provide you with customer support.
          <div className="mt-4">
            We use contact information and payment information you provide on
            our mobile application to provide you the products you order, obtain
            payment for our products, and provide customer support. For the
            account associated with the mobile application, we use your email
            address and password to provide and control access to your account.
          </div>
          <div className="mt-4">
            Regarding purchases made with Medcursor’s mobile application,
            Medcursor collects contact information and payment information you
            provide to provide you the applications you order, obtain payment
            for our applications or services, and provide customer support. For
            application account information, we use your email address and
            password to provide and control access to your online applications
            and account.
          </div>
          <div className="mt-4">
            Our support team may assist you by resetting your password or
            updating your profile information.
          </div>
          <div className="mt-4">
            If you have opted to receive our newsletter and other periodic email
            communications, we will send you our newsletter and other marketing
            communications. Also, we track purchase and application download
            history to understand our users’ preferences, plan for future sales,
            and understand overall demand for our products.
          </div>
          <div className="font-bold mt-4 pb-2">ii. Uses of User Data</div>
          We collect device data to register your device for our services, to
          provide Medcursor’s services, and for internal purposes such as
          auditing, data analysis, loss prevention, and fraud prevention. We
          also use device data to provide, maintain, develop, operate, deliver,
          and improve our products and services provided to you. We access user
          data collected by Medcursor-compatible devices only on a “need to
          know” basis, for instance to respond to your support requests or to
          respond to a security incident.
          <div className="mt-4">
            When a user obtains support from us, we may use your Internet
            Protocol (IP) address in order to determine whether your smart
            device is functioning properly and to provide user support.
            Likewise, we use your location information, such as longitude and
            latitude, time zone settings, region, country, etc., as well as the
            history of operation of the Medcursor-compatible smart devices you
            purchase, including errors, history of logging in, your schedule
            settings, and input records, for purposes of user support. We may
            also collect technical information to help us identify your device
            for diagnostic purposes and fraud prevention.
          </div>
        </div>
        <div className="font-bold mt-4">
          d. Uses of Information Collected By Our Social Media Pages
        </div>
        <div className="p-2">
          We use the information you post on our social media pages and your
          profile information to understand the preferences of our users related
          to our current and future services and product lines. We also analyze
          feedback on the quality of our applications and related services to
          improve our applications and services. We are also interested in
          requests for future applications and services.
        </div>
        <div className="font-bold mt-4">
          e. Retention of Personal Information
        </div>
        <div className="p-2">
          We will maintain your personal information only as long as it is
          needed for the purposes described above in this Section 5, such as to
          complete transactions with you, to complete any applicable
          investigations, for the duration of any dispute resolution process, or
          to the extent required to meet preservation requirements under
          applicable law. Medcursor may continue to maintain your personal
          information for the reasons described in Section 7(a)(ii)(2) below.
          <div className="mt-4">
            You may delete your Medcursor online account at any time. After you
            delete your online account, Medcursor will delete the personal
            information stored in it, including any stored payment information
            or personal information collected by product partners’ smart
            devices.
          </div>
        </div>
        <div className="font-bold mt-4">f. With Your Consent</div>
        <div className="p-2">
          We may share your information for other purposes with your consent or
          direction. We collaborate with third-party services such as{" "}
          <span className="font-bold">Facebook</span>,{" "}
          <span className="font-bold">Apple</span>, and{" "}
          <span className="font-bold">Google</span> to offer you a seamless{" "}
          <span className="font-bold">sign-up</span>,{" "}
          <span className="font-bold">login</span>, and{" "}
          <span className="font-bold">content-sharing</span> experience. We may
          share information about you with these third-party services if you opt
          to use these features. For instance, these services can receive
          information about your platform activity and inform your connections
          on the third-party services about your platform usage, all according
          to their privacy policies. If you allow a third-party service to
          access your account, we will share specific information about you with
          the third party. Depending on the permissions you grant, the
          third-party service may gain access to your account information and
          any other information you supply.
          <div className="mt-4">
            When signing in through third-party platforms like Facebook, Apple,
            and Google, we will only access your name and email information for
            app login and registration purposes. We take your privacy very
            seriously and will not disclose your details to third parties
            without your explicit approval. Moreover, we will not use your
            information for third-party applications unless you give us the
            go-ahead or actively use the feature.
          </div>
          <p className="mt-4 mb-2">
            The following description details how we handle data in relation to
            Google accounts:
          </p>
          <h3 className="font-bold mt-3 mb-1">1. Data Access and Usage:</h3>
          <p>
            Medcursor uses Google account login to authenticate users. We only
            collect the following information:
          </p>
          <div>
            Basic personal details provided by your Google account (such as your
            name and email address).
          </div>
          <div>
            We utilize this information to furnish you with services like a
            personalized account experience and simplified login procedures. We
            assure you that this information will not be employed for any other
            purposes.
          </div>
          <h3 className="font-bold mt-3 mb-1"> 2. Data Storage:</h3>
          <div>
            Personal information acquired through the Google account is stored
            on secure servers. We implement adequate security measures to
            safeguard your personal data against loss, misuse, unauthorized
            access, or disclosure. Personal details are retained only for the
            duration necessary to deliver the service. Should you decide to
            deactivate your account, we will expunge the stored data.
          </div>
          <h3 className="font-bold mt-3 mb-1">3. Data Sharing:</h3>
          <div>
            We do not share your personal information with third parties unless
            we have your explicit consent, or to fulfill legal obligations,
            respond to court orders, or government demands.
          </div>
          <h3 className="font-bold mt-3 mb-1">4. User Control:</h3>
          <div>
            You can manage your data anytime through the settings of your Google
            account. If you opt to terminate your Medcursor account, we will
            erase all related information from your account.
          </div>
        </div>
      </div>
    </Row>
  );
};
